import { Box } from '@material-ui/core'
import classNames from 'classnames'
import { MessageSquareText } from 'lucide-react'
import { useCallback, useRef } from 'react'
import { useOutsideClick } from 'src/modules/core/hooks/use-outside-click'
import { inboundNotificationsService } from 'src/services/inbound-notifications.service'
import { InboundNotificationsBadge } from './inbound-notifications-badge'
import { InboundNotificationsPanel } from './inbound-notifications-panel'
import { useInboundNotifications } from '../context/inbound-notifications-context'
import classes from './inbound-notifications-panel.module.scss'

type Props = {
  className?: string
  toggleClassName?: string
}

export function InboundNotificationsDrawer({ className, toggleClassName }: Props) {
  const drawerRootRef = useRef<HTMLDivElement>(null)
  const {
    isOpen,
    toggleCenter,
    closeCenter,
    clearFilter,
    unreadCount,
    notifications,
    filterEventId,
    markAllRead,
    toggleReadState,
    refreshNotifications,
  } = useInboundNotifications()

  const handleOutsideClick = useCallback(() => {
    if (isOpen) {
      closeCenter()
    }
  }, [closeCenter, isOpen])

  useOutsideClick([drawerRootRef], handleOutsideClick)

  const visibleNotifications = filterEventId
    ? notifications.filter((notification) => notification.event_id === filterEventId)
    : notifications

  const hasUnreadVisible = visibleNotifications.some((notification) => notification.read_at == null)

  const handleMarkAllRead = useCallback(async () => {
    if (filterEventId) {
      await inboundNotificationsService.bulkRead(filterEventId)
      await refreshNotifications()
      return
    }
    await markAllRead()
  }, [filterEventId, markAllRead, refreshNotifications])

  const toggleAriaLabel = unreadCount > 0 ? `Notifications, ${unreadCount} unread messages` : 'Notifications'

  return (
    <div ref={drawerRootRef} className={classNames(classes.drawerRoot, className)}>
      <button
        type="button"
        className={classNames(classes.drawerToggle, toggleClassName)}
        onClick={toggleCenter}
        aria-label={toggleAriaLabel}
        aria-expanded={isOpen}
      >
        <MessageSquareText size={20} aria-hidden />
        <InboundNotificationsBadge count={unreadCount} />
      </button>
      {isOpen ? (
        <Box className={classes.drawerPanel}>
          {filterEventId ? (
            <button type="button" className={classes.clearFilter} onClick={clearFilter}>
              Show all notifications
            </button>
          ) : null}
          {hasUnreadVisible ? (
            <Box className={classes.drawerHeader}>
              <button type="button" className={classes.markAllRead} onClick={() => void handleMarkAllRead()}>
                Mark all as read
              </button>
            </Box>
          ) : null}
          <InboundNotificationsPanel notifications={visibleNotifications} onToggleRead={toggleReadState} />
        </Box>
      ) : null}
    </div>
  )
}
